import assignIn from 'lodash/assignIn';
import isEmpty from 'lodash/isEmpty';
import isPlainObject from 'lodash/isPlainObject';
import log from '@atlassian/jira-common-util-logging/src/log.tsx';
import { captureException } from '@atlassian/jira-capture-exception/src/index.tsx';
import { fireTrackAnalytics as fireTrackAnalyticsOld } from '@atlassian/jira-analytics-web-react/src/utils/fire-track-event.tsx';
import { getFieldLengthForAnalytics } from '@atlassian/jira-platform-board-kit/src/common/utils/field-length-for-analytics/index.tsx';
import { fireTrackAnalytics } from '@atlassian/jira-product-analytics-bridge';
import { fg } from '@atlassian/jira-feature-gating';
import type { State } from '../../reducers/types.tsx';
import {
	getIsCompanyManaged,
	getRapidViewId,
	projectIdSelector,
} from '../../selectors/app/index.tsx';
import type { MiddlewareAPI, Action, Dependencies } from '../../types.tsx';
import getActionAnalyticsMap, { ANALYTIC_PREFIX } from './actions-map.tsx';
import { triggerAnalytics } from './trigger-analytics.tsx';
import type { AnalyticPayload } from './types.tsx';

const APP_ANALYTICS_PREFIX = 'software-plan-view';

// @ts-expect-error - TS2525 - Initializer provides no value for this binding element and the binding element has no default value.
const shouldTriggerAnalytics = ({ type } = {}) => getActionAnalyticsMap()[type] !== undefined;

const getAnalyticsForAction = (
	preActionState: State,
	postActionState: State,
	action: Action,
	dependencies?: Dependencies,
): AnalyticPayload[] =>
	getActionAnalyticsMap()
		[action.type].map((createAnalytic) =>
			createAnalytic(ANALYTIC_PREFIX)(preActionState, postActionState, action, dependencies),
		)
		.filter((analyticEvent) => analyticEvent != null);

const triggerAnalyticsForAction = (analyticEvents: AnalyticPayload[], state: State) => {
	const triggerSoftwareAnalytics = triggerAnalytics(APP_ANALYTICS_PREFIX);

	if (isEmpty(analyticEvents)) {
		return;
	}

	analyticEvents.forEach(({ name, data = {}, trackEvents = [] }) => {
		assignIn(data, {
			rapidViewId: getRapidViewId(state),
			projectId: projectIdSelector(state),
			isCompanyManaged: getIsCompanyManaged(state),
		});

		// This is not doing anything, we can remove it as part of
		// https://hello.atlassian.net/browse/CATB-418
		triggerSoftwareAnalytics(name, data);

		trackEvents.forEach((trackEvent) => {
			const { payload } = trackEvent;
			const { action, actionSubject, card, ...otherProperties } = payload;
			/**
			 * Fields inputted by the user. When this was created, there was no ability to
			 * add in extra fields in the inline create, so we'll assume it's ONLY summary.
			 */
			const issueCreateFieldsUsed = payload.issueCreateFieldsUsed ?? [
				{
					gqlType: 'JiraSingleLineTextField',
					issueFieldId: 'summary',
					issueFieldType: 'summary',
					isPopulated: true,
				},
			];

			let fieldsPopulated = {};

			if (payload.sessionId !== undefined && !!card) {
				// Fields that can be populated automatically e.g. by filters, swimlanes, postfunctions
				fieldsPopulated = {
					ASSIGNEE: !!card.assignee,
					PARENT: !!card.parentId,
					PRIORITY: !!card.priority,
					FIX_VERSIONS: getFieldLengthForAnalytics(card.fixVersions?.length),
					LABELS: getFieldLengthForAnalytics(card.labels?.length),
				};
			}

			/**
			 * Facilitate CMP instrumentation for inline creation analytics.
			 *
			 * https://hello.atlassian.net/wiki/spaces/SQE/pages/2613558743/Instrumentation+Spec+Inline+Create+CMP+Boards - ERAI-1376
			 *
			 * The following assumptions are made due to how inline creation is made;
			 * - the only issue create field that is present: summary
			 * - It has to be "backlog" for the source of inline creation, if this is altered in the future, this will not track other sources.
			 */
			const additionalPayloads = {
				...otherProperties,
				isCompanyManaged: getIsCompanyManaged(state),
				issueCreateFieldsUsed,
				fieldsPopulated,
			};

			/**
			 * NOTE
			 *
			 * This is a temporary fix for https://hello.atlassian.net/browse/CATB-415
			 *
			 * It should be cleaned up by https://hello.atlassian.net/browse/CATB-418
			 */
			if (action && actionSubject) {
				fireTrackAnalytics(trackEvent, `${actionSubject} ${action}`, {
					...additionalPayloads,
				});
			} else {
				fireTrackAnalyticsOld(trackEvent, {
					action: payload.action,
					attributes: {
						...additionalPayloads,
					},
				});
			}
		});
	});
};

// eslint-disable-next-line jira/import/no-anonymous-default-export
export default (dependencies?: Dependencies) =>
	(store: MiddlewareAPI) =>
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	(next: (action: Action) => any) =>
	(action: Action) => {
		if (!isPlainObject(action) && fg('jsw-backlog-redux-middleware-error-log')) {
			const fullLocation = 'common.redux-error.software.backlog';
			const error = new Error(`Action is not a plain object: '${JSON.stringify(action)}'`);
			log.safeErrorWithoutCustomerData(
				fullLocation,
				`Action is not a plain object: '${JSON.stringify(action)}'`,
				error,
			);
			captureException(fullLocation, error);
			return;
		}

		if (!shouldTriggerAnalytics(action)) {
			next(action);
			return;
		}

		const preActionState = store.getState();

		next(action);

		const postActionState = store.getState();

		const analyticEvents = getAnalyticsForAction(
			preActionState,
			postActionState,
			action,
			dependencies,
		);

		triggerAnalyticsForAction(analyticEvents, postActionState);
	};
